import {
  alpha,
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  InputAdornment,
  Pagination,
  Paper,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TableItem from "../../../components/TableItem";
import React, { useEffect, useState } from "react";
import {
  deleteStockIntakeById,
  getStockUpdateData,
} from "../../../services/Import";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useHistory } from "react-router";
import InputField from "../../../FormComponents/InputField";
import StockUpdateForm from "./Im_stockUpdateForm";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddHomeIcon from "@mui/icons-material/AddHome";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import ConfirmationPopup from "../../../components/ConfirmationPopup";

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.shape.borderRadius * 1.5,
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(3),
  boxShadow: "0 8px 16px 0 rgba(0,0,0,0.1)",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 1.5,
  textTransform: "none",
  fontWeight: 600,
  boxShadow: "0 4px 12px 0 rgba(0,0,0,0.1)",
}));

const FilterContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius * 1.5,
  backgroundColor: alpha(theme.palette.primary.light, 0.05),
  marginBottom: theme.spacing(3),
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  padding: theme.spacing(2),
  marginTop: theme.spacing(2),
}));

// Stock Update Type options
const stockUpdateTypeOptions = [
  { value: "", label: "All Types" },
  { value: "MATERIAL_BILL", label: "Material Bill" },
  { value: "STOCK_INTAKE_MANUAL_UPDATE", label: "Stock Intake Manual Update" },
  { value: "INVENTORY_MANUAL_UPDATE", label: "Inventory Manual Update" },
  { value: "PRODUCTION", label: "Production" },
  { value: "SUB_ORDER", label: "Sub Order" },
];

// Deprecated Status options
const deprecatedStatusOptions = [
  { value: "", label: "All Records" },
  { value: "false", label: "Active Only" },
  { value: "true", label: "Deprecated Only" },
  { value: "null", label: "Not Set" },
];

let _stockUpdateListState = null;

function Im_stockupdates() {
  const history = useHistory();
  const [rows, setRows] = React.useState([]);
  const [isLoading, setLoading] = React.useState(false);
  const [totalRows, setTotalRows] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [openForm, setOpenForm] = React.useState(false);

  // Confirmation state for delete action
  const [isMatched, setIsMatched] = useState(false);
  const [matchingText, setMachingText] = useState("");
  const [confirmationText, setConfirmationText] = useState("");
  const [deletingRow, setDeletingRow] = useState("");
  const [openPopup, setOpenPopup] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        if (_stockUpdateListState) {
          const { page: savedPage, filter: savedFilter } = _stockUpdateListState;
          _stockUpdateListState = null;
          setPage(savedPage);
          setFilter(savedFilter);
          await fetchData(savedFilter, savedPage);
        } else {
          await fetchData();
        }
      } catch (err) {
        if (isMounted) console.error("Failed to load stock updates:", err);
      }
    };
    load();
    return () => { isMounted = false; };
  }, []);

  // Effect for deletion confirmation
  useEffect(() => {
    if (isMatched && !openPopup && deletingRow !== null) {
      handleDeleteStockUpdate(deletingRow.action);
    }
  }, [isMatched, openPopup]);

  // Columns for table with new deprecated column
  const columns = [
    { id: "date", label: "Date", minWidth: 80 },
    { id: "item", label: "Item", minWidth: 100 },
    { id: "brand", label: "Brand", minWidth: 100 },
    { id: "type", label: "Update Type", minWidth: 120 },
    {
      id: "qty",
      label: "Qty",
      minWidth: 80,
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "total",
      label: "Total",
      minWidth: 100,
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
    },
    { id: "action", label: "Actions", minWidth: 100 },
  ];

  function createData(
    date,
    item,
    brand,
    type,
    qty,
    total,
    status,
    isDeprecated,
    action,
    id
  ) {
    return {
      date,
      item,
      brand,
      qty,
      type,
      total,
      status,
      isDeprecated,
      action,
      id,
    };
  }

  const handleChange = async (event, value) => {
    setPage(value - 1);
    await fetchData(filter, value - 1);
  };

  // Fetch data for pagination actions
  const fetchData = async (customFilter = null, customPage = null) => {
    setLoading(true);
    const currentFilter = customFilter || filter;
    const currentPage = customPage !== null ? customPage : page;

    let result;
    try {
      result = await getStockUpdateData(currentPage, rowsPerPage, currentFilter);
    } catch (err) {
      setLoading(false);
      return;
    }
    if (!result || typeof result !== "object" || !Array.isArray(result.content)) {
      setLoading(false);
      return;
    }
    let stockUpdateSet = result.content;

    // Set total rows and pages
    setTotalPages(result.totalPages);
    setTotalRows(result.totalElements);

    const newSet = [];
    if (stockUpdateSet) {
      for (let x = 0; x < stockUpdateSet.length; x++) {
        const updateTypeKey = stockUpdateSet[x].updateType;
        const updateTypeLabel =
          stockUpdateTypeOptions.find((opt) => opt.value === updateTypeKey)
            ?.label || updateTypeKey;

        const isDeprecated = stockUpdateSet[x].isDeprecated || false;

        // Create status chip component
        const statusChip = isDeprecated ? (
          <Chip
            icon={<WarningAmberIcon />}
            label="Deprecated"
            size="small"
            color="error"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<CheckCircleIcon />}
            label="Active"
            size="small"
            color="success"
            variant="outlined"
          />
        );

        newSet.push(
          createData(
            stockUpdateSet[x].addedDate,
            stockUpdateSet[x].imports.itemName,
            stockUpdateSet[x].imports.brand,
            updateTypeLabel,
            stockUpdateSet[x].qty,
            stockUpdateSet[x].totalPrice,
            statusChip,
            isDeprecated,
            stockUpdateSet[x].intakeId
          )
        );
      }
    }

    // Set rows to table
    setRows(newSet);
    setLoading(false);
  };

  // Filter values with new fields
  const [filter, setFilter] = useState({
    _productCode: "",
    _itemName: "",
    _brand: "",
    _addedBy: "",
    _billNo: "",
    _isDeprecated: "", // New: Deprecated status filter
    _stockUpdateType: "", // New: Stock update type filter
  });

  // Reset filters
  const resetFilters = () => {
    const resetFilter = {
      _productCode: "",
      _itemName: "",
      _brand: "",
      _addedBy: "",
      _billNo: "",
      _isDeprecated: "",
      _stockUpdateType: "",
    };
    setFilter(resetFilter);
    fetchData(resetFilter, page);
  };

  // Handle changing filters
  const handleFilterChange = (name, val) => {
    setFilter({
      ...filter,
      [name]: val,
    });
  };

  const handleAction = (event, id) => {
    switch (event) {
      case "view":
        _stockUpdateListState = { page, filter };
        history.push("/template/stock_update_view/" + id);
        break;
      case "delete":
        handleDeleteConfirmation(id);
        break;
      default:
        return "foo";
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirmation = (id) => {
    setIsMatched(false);
    let filterData = rows.find((row) => row.action === id);
    setDeletingRow(filterData);
    let topicText = `Please confirm item name to delete: ${filterData.item}`;
    setConfirmationText(topicText);
    setMachingText(filterData.item);
    setOpenPopup(true);
  };

  const handleDeleteStockUpdate = async (id) => {
    setLoading(true);
    let deleteStockIntakeResponse = await deleteStockIntakeById(id);
    if (deleteStockIntakeResponse) {
      fetchData();
      setLoading(false);
    } else {
      setLoading(false);
      console.error("Failed to delete stock update");
    }
  };

  // Confirmation popup handler
  const handleOpenPopup = () => {
    setOpenPopup(!openPopup);
  };

  // Toggle filter visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <StyledPaper elevation={0}>
      {openForm ? (
        <StockUpdateForm setOpenForm={setOpenForm} />
      ) : (
        <>
          <HeaderCard>
            <Grid container alignItems="center" spacing={2}>
              <Grid item>
                <AddHomeIcon fontSize="large" />
              </Grid>
              <Grid item xs>
                <Typography variant="h5" fontWeight="bold">
                  Import Stock Updates Management
                </Typography>
                <Typography variant="body2">
                  View and manage Import Stock Updates and tags
                </Typography>
              </Grid>
              <Grid item>
                <RoleBasedRender
                  allowedRoles={["IMPORT_STOCK_CREATE", "ROLE_ADMIN"]}
                >
                  <ActionButton
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenForm(true)}
                  >
                    Add Stock Update
                  </ActionButton>
                </RoleBasedRender>
              </Grid>
            </Grid>
          </HeaderCard>

          <Box
            mb={3}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <TextField
              placeholder="Search by item name..."
              variant="outlined"
              size="small"
              sx={{ width: "40%" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={(e) => {
                handleFilterChange("_itemName", e.target.value);
                if (e.target.value.length > 2 || e.target.value.length === 0) {
                  fetchData({ ...filter, _itemName: e.target.value }, page);
                }
              }}
            />
            <Stack direction="row" spacing={2}>
              <Tooltip title="Toggle Filters">
                <ActionButton
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={toggleFilters}
                >
                  Filters
                </ActionButton>
              </Tooltip>
              <Chip
                label={`${totalRows} Stock Updates`}
                variant="outlined"
                color="primary"
              />
            </Stack>
          </Box>

          {showFilters && (
            <FilterContainer>
              <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                Advanced Filters
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_productCode"
                    value={filter._productCode}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Product Code"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_itemName"
                    value={filter._itemName}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Item Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_brand"
                    value={filter._brand}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Brand"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_addedBy"
                    value={filter._addedBy}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Added By"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <InputField
                    name="_billNo"
                    value={filter._billNo}
                    onChange={(event, newInputValue) =>
                      handleFilterChange(event, newInputValue)
                    }
                    type="text"
                    label="Bill Number"
                  />
                </Grid>

                {/* NEW: Stock Update Type Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="stock-update-type-label">
                      Update Type
                    </InputLabel>
                    <Select
                      labelId="stock-update-type-label"
                      id="stock-update-type-select"
                      value={filter._stockUpdateType}
                      label="Update Type"
                      onChange={(e) =>
                        handleFilterChange("_stockUpdateType", e.target.value)
                      }
                    >
                      {stockUpdateTypeOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* NEW: Deprecated Status Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="deprecated-status-label">
                      Record Status
                    </InputLabel>
                    <Select
                      labelId="deprecated-status-label"
                      id="deprecated-status-select"
                      value={filter._isDeprecated}
                      label="Record Status"
                      onChange={(e) =>
                        handleFilterChange("_isDeprecated", e.target.value)
                      }
                    >
                      {deprecatedStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <Box mt={2} display="flex" justifyContent="flex-end">
                <Stack direction="row" spacing={2}>
                  <Tooltip title="Reset Filters">
                    <ActionButton
                      variant="outlined"
                      color="secondary"
                      startIcon={<RestartAltIcon />}
                      onClick={resetFilters}
                    >
                      Reset
                    </ActionButton>
                  </Tooltip>
                  <Tooltip title="Apply Filters">
                    <ActionButton
                      variant="contained"
                      color="primary"
                      onClick={() => fetchData(filter, page)}
                    >
                      Apply Filters
                    </ActionButton>
                  </Tooltip>
                </Stack>
              </Box>
            </FilterContainer>
          )}

          <Divider sx={{ my: 2 }} />

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 5 }}>
              <Typography variant="body1" color="textSecondary">
                No stock updates found. Try adjusting your filters or add a new
                stock update.
              </Typography>
            </Box>
          ) : (
            <TableItem
              columns={columns}
              rows={rows}
              page={page}
              setPage={setPage}
              tablePagin={true}
              totalPages={totalPages}
              getData={fetchData}
              handleAction={handleAction}
              showActions={{
                view: {
                  label: "Stock update view",
                },
                delete: {
                  label: "Delete stock update",
                  condition: (row) => !row.isDeprecated, // Only allow delete for non-deprecated
                },
              }}
              actionRoles={{
                view: ["IMPORT_STOCK_VIEW", "ROLE_ADMIN"],
                delete: ["IMPORT_STOCK_DELETE", "ROLE_ADMIN"],
              }}
              // NEW: Custom row styling for deprecated rows
              rowStyle={(row) => ({
                backgroundColor: row.isDeprecated
                  ? alpha("#ff9800", 0.1) // Orange background for deprecated
                  : "transparent",
                opacity: row.isDeprecated ? 0.7 : 1,
              })}
            />
          )}

          {totalPages > 1 && (
            <PaginationContainer>
              <Pagination
                count={totalPages}
                page={page + 1}
                variant="outlined"
                shape="rounded"
                color="primary"
                onChange={handleChange}
                size="medium"
              />
            </PaginationContainer>
          )}
        </>
      )}

      {openPopup && (
        <ConfirmationPopup
          setOpenPopup={setOpenPopup}
          openPopup={openPopup}
          handleOpenPopup={handleOpenPopup}
          matchingText={matchingText}
          message={confirmationText}
          isMatched={isMatched}
          setIsMatched={setIsMatched}
        />
      )}
    </StyledPaper>
  );
}

export default Im_stockupdates;
